import { CheckCircle2, Loader2, XCircle, SkipForward } from 'lucide-react';
import { Layout, Database, GitBranch, MousePointerClick } from 'lucide-react';
import { FOREST_GREEN, MID_GRAY, ERROR, BORDER, SHADOWS, WHITE } from '@/styles/tokens';
import { PLAN_DOMAIN_ICONS, PLAN_DOMAIN_LABELS } from '@/types/plan';
import type { Plan, PlanSectionDomain } from '@/types/plan';
import type { LucideIcon } from 'lucide-react';

const DOMAIN_ICON_COMPONENTS: Record<string, LucideIcon> = { Layout, Database, GitBranch, MousePointerClick };

function getDomainIcon(domain: PlanSectionDomain): LucideIcon {
  const iconName = PLAN_DOMAIN_ICONS[domain];
  return DOMAIN_ICON_COMPONENTS[iconName] ?? Layout;
}

function SectionStatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="w-4 h-4" style={{ color: FOREST_GREEN.DEFAULT }} />;
    case 'executing':
      return <Loader2 className="w-4 h-4 animate-spin" style={{ color: FOREST_GREEN.DEFAULT }} />;
    case 'failed':
      return <XCircle className="w-4 h-4" style={{ color: ERROR.DEFAULT }} />;
    case 'skipped':
      return <SkipForward className="w-4 h-4" style={{ color: MID_GRAY.DEFAULT }} />;
    default: // pending
      return (
        <div
          style={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            border: `2px solid ${MID_GRAY.lighter}`,
            boxSizing: 'border-box',
          }}
        />
      );
  }
}

interface PlanExecutionProgressProps {
  plan: Plan;
  currentSectionId?: string;
  errors: Record<string, string>;
  onCancel: () => void;
}

export function PlanExecutionProgress({ plan, currentSectionId, errors, onCancel }: PlanExecutionProgressProps) {
  const sections = Array.isArray(plan.sections) ? plan.sections : [];
  return (
    <div
      aria-live="polite"
      style={{
        border: `1px solid ${BORDER.DEFAULT}`,
        borderRadius: 8,
        background: WHITE,
        boxShadow: SHADOWS.sm,
        padding: 16,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Executing Plan...</div>

      {/* Section list */}
      <div style={{ position: 'relative', paddingLeft: 20 }}>
        {/* Vertical connecting line */}
        <div
          style={{
            position: 'absolute',
            left: 7,
            top: 16,
            bottom: 16,
            width: 2,
            background: BORDER.DEFAULT,
          }}
        />

        {sections.map((section, index) => {
          const DomainIcon = getDomainIcon(section.domain);
          const status = section.id === currentSectionId && section.status === 'pending'
            ? 'executing'
            : section.status;
          const sectionError = errors[section.id];

          return (
            <div
              key={section.id}
              style={{
                position: 'relative',
                paddingBottom: index < plan.sections.length - 1 ? 12 : 0,
              }}
            >
              {/* Status icon (overlays the vertical line) */}
              <div
                style={{
                  position: 'absolute',
                  left: -20,
                  top: 2,
                  background: WHITE,
                  zIndex: 1,
                }}
              >
                <SectionStatusIcon status={status} />
              </div>

              <div className="flex items-center gap-2">
                <DomainIcon style={{ width: 14, height: 14, color: FOREST_GREEN.DEFAULT }} />
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: status === 'executing' ? 600 : 400,
                    color: status === 'pending' ? MID_GRAY.lighter : undefined,
                    flex: 1,
                  }}
                >
                  {PLAN_DOMAIN_LABELS[section.domain]}
                </span>
                <span style={{ fontSize: 11, color: MID_GRAY.lighter }}>
                  {status === 'completed' && 'done'}
                  {status === 'executing' && 'executing'}
                  {status === 'failed' && 'failed'}
                  {status === 'skipped' && 'skipped'}
                </span>
              </div>

              {/* Error message */}
              {status === 'failed' && sectionError && (
                <div
                  style={{
                    marginTop: 4,
                    padding: '4px 8px',
                    borderRadius: 4,
                    background: ERROR.bg,
                    fontSize: 11,
                    color: ERROR.DEFAULT,
                    lineHeight: 1.5,
                  }}
                >
                  {sectionError}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Cancel button */}
      <div style={{ marginTop: 16 }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: '8px 20px',
            borderRadius: 6,
            border: `1px solid ${ERROR.DEFAULT}`,
            background: 'transparent',
            color: ERROR.DEFAULT,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
